import type {
  ConnectorCatalogItem,
  ConnectorSummary,
  ListConnectorCatalogResponse,
  ListConnectorsResponse,
  UpdateConnectorReq,
  WhatsappConnectorStatusResponse,
  WhatsappSyncLinkReq,
  WhatsappSyncLinkResponse,
  WhatsappSyncStatusResponse,
} from "@/types/connectors.ts";
import type { RemovePhoneNumberResp } from "@/types/accounts.ts";
import type { ConnectImapReq, ConnectImapResp, ImapPresetLookupResp } from "@/types/imap.ts";
import { IMAP_PRESETS_PATH } from "@/types/imap.ts";
import type {
  RegisterCustomMcpServerReq,
  RegisterCustomMcpServerResponse,
  SuggestCustomMcpServerReq,
  SuggestCustomMcpServerResponse,
  UpdateCustomMcpServerReq,
  UpdateCustomMcpServerResponse,
} from "@/types/mcp.ts";
import { queryOptions, skipToken } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { resolveApiPath } from "@/lib/utils.ts";
import { connectorReauthPath } from "@/shared/connectors/reauth.ts";
import { PROVIDER_DISPLAY_NAMES } from "@/shared/connectors/providers.ts";

/**
 * Re-exported from the shared table so this module's existing importers keep
 * their import path. Native reads the same table, which is what stops the two
 * from drifting the way web's own two copies already had.
 */
export const providerDisplayNames: Record<string, string> = PROVIDER_DISPLAY_NAMES;

export const getConnectorConnectUrl = (
  provider: "google" | "microsoft" | "zoom" | "slack" | "teams" | "gdrive",
): string => resolveApiPath(`/api/a/connectors/connect/${provider}`);

/** Connect an additional email inbox (a second mailbox in the current workspace). */
export const getConnectorEmailInboxUrl = (provider: "google" | "microsoft"): string =>
  resolveApiPath(`/api/a/connectors/connect-email-inbox/${provider}`);

export function getReauthUrl(connector: ConnectorSummary): string {
  return resolveApiPath(connectorReauthPath(connector));
}

export const getConnectors = async (): Promise<ListConnectorsResponse> => {
  return (await api.get<ListConnectorsResponse>("/api/a/connectors")).data;
};

/**
 * The user's connected accounts. Shares the `["connectors"]` key with the older
 * inline call sites on purpose — see `calendarQueries.list`.
 */
export const connectorQueries = {
  list: () =>
    queryOptions({
      queryKey: ["connectors"] as const,
      queryFn: async ({ signal }) => {
        return (await api.get<ListConnectorsResponse>("/api/a/connectors", { signal })).data;
      },
    }),
};

/**
 * User-facing dynamic connector catalog: admin-enabled MCP servers and API
 * specs, plus the category vocabulary for the gallery's filter list.
 */
export const getConnectorCatalog = async (): Promise<ListConnectorCatalogResponse> => {
  return (await api.get<ListConnectorCatalogResponse>("/api/a/connectors/catalog")).data;
};

export const deleteConnector = async (connectorId: number): Promise<void> => {
  await api.post(`/api/a/connectors/${connectorId}/delete`, {});
};

export const updateConnectorDisplayName = async (
  connectorId: number,
  data: UpdateConnectorReq,
): Promise<void> => {
  await api.post(`/api/a/connectors/${connectorId}/update`, data);
};

export const triggerConnectorSync = async (connectorId: number): Promise<void> => {
  await api.post(`/api/a/connectors/${connectorId}/sync`, {});
};

/**
 * Browser-usable target for a catalog item's connect flow.
 *
 * `connectPath` is chosen by the server and may be either origin-relative or
 * mount-relative. `resolveApiPath` accepts both forms without duplicating the
 * `/api` mount.
 */
export const getConnectorCatalogConnectUrl = (item: ConnectorCatalogItem): string | null =>
  item.connectPath ? resolveApiPath(item.connectPath) : null;

export const connectConnectorCatalogItemWithApiKey = async (
  item: ConnectorCatalogItem,
  apiKey: string,
): Promise<void> => {
  if (!item.credentialSubmitPath) throw new Error("Connector does not accept API-key credentials");
  await api.postServerPath(item.credentialSubmitPath, { apiKey });
};

export const connectConnectorCatalogItemWithCredentials = async (
  item: ConnectorCatalogItem,
  credentials: Record<string, unknown>,
): Promise<void> => {
  if (!item.credentialSubmitPath) throw new Error("Connector does not accept credential JSON");
  await api.postServerPath(item.credentialSubmitPath, { credentials });
};

// ── Custom MCP servers: a user registers a server for their own account ─────

/** Probe a URL before adding it: suggested name and copy, detected auth, and whether it is already known. */
export const suggestCustomMcpServer = async (
  req: SuggestCustomMcpServerReq,
): Promise<SuggestCustomMcpServerResponse> =>
  (await api.post<SuggestCustomMcpServerResponse>("/api/a/mcp/custom/suggest", req)).data;

/** Register a server for this account; with `apiKey` it connects in the same call. */
export const registerCustomMcpServer = async (
  req: RegisterCustomMcpServerReq,
): Promise<RegisterCustomMcpServerResponse> =>
  (await api.post<RegisterCustomMcpServerResponse>("/api/a/mcp/custom", req)).data;

export const updateCustomMcpServer = async (
  serverId: number,
  req: UpdateCustomMcpServerReq,
): Promise<UpdateCustomMcpServerResponse> =>
  (await api.post<UpdateCustomMcpServerResponse>(`/api/a/mcp/custom/${serverId}`, req)).data;

/** Removes the server, its connection, and the words the assistant learned for it. */
export const deleteCustomMcpServer = async (serverId: number): Promise<void> => {
  await api.post(`/api/a/mcp/custom/${serverId}/delete`, {});
};

/** What the IMAP connect form tells the user about an address before asking for a password. */
export const imapPresetQueries = {
  lookup: (email: string | undefined) =>
    queryOptions({
      queryKey: ["imapPreset", email] as const,
      queryFn: email
        ? async ({ signal }) => {
            const response = await api.get<ImapPresetLookupResp>(
              `${IMAP_PRESETS_PATH}?email=${encodeURIComponent(email)}`,
              { signal },
            );
            return response.data;
          }
        : skipToken,
      // A malformed address is a validation 400, not worth retrying.
      retry: false,
    }),
};

/** `submitPath` is the catalog item's `credentialSubmitPath`. */
export const connectImapMailbox = async (
  submitPath: string,
  req: ConnectImapReq,
): Promise<ConnectImapResp> => (await api.postServerPath<ConnectImapResp>(submitPath, req)).data;

/**
 * WhatsApp connector status: link/verification state plus the okthx company
 * number for "message okthx on WhatsApp" deep links.
 */
export const getWhatsappConnectorStatus = async (): Promise<WhatsappConnectorStatusResponse> => {
  return (await api.get<WhatsappConnectorStatusResponse>("/api/a/connectors/whatsapp/status")).data;
};

/**
 * Device-link status for the WhatsApp connector (session state + the QR to
 * render mid-link), from the first-party session host. The path predates the
 * connector unification.
 */
export const getWhatsappSyncStatus = async (): Promise<WhatsappSyncStatusResponse> => {
  return (await api.get<WhatsappSyncStatusResponse>("/api/a/connectors/whatsapp-sync/status")).data;
};

/**
 * Start a device link. Empty body = QR flow, polled via
 * {@link getWhatsappSyncStatus}; with `phoneNumber`, the response carries a
 * pairing code the user types into WhatsApp under Linked Devices.
 *
 * An empty body is also the resume: the host re-serves a pairing code it
 * already has in flight, so a remount can't invalidate the code being typed.
 * Send `refresh: true` to say that code did not work and a new one is needed —
 * without it the retry gets the same string back.
 */
export const startWhatsappSyncLink = async (
  req: WhatsappSyncLinkReq,
): Promise<WhatsappSyncLinkResponse> => {
  return (await api.post<WhatsappSyncLinkResponse>("/api/a/connectors/whatsapp-sync/link", req))
    .data;
};

export const unlinkWhatsappSync = async (): Promise<void> => {
  await api.post("/api/a/connectors/whatsapp-sync/unlink", {});
};

/**
 * Remove the Text Messages connector, which removes the account's verified
 * phone number. Refused `409 PHONE_ONLY_SIGN_IN` when the number is the only
 * way to sign in.
 */
export const removeTextMessagesPhone = async (): Promise<void> => {
  await api.post<RemovePhoneNumberResp>("/api/a/users/me/onboarding/phone/remove");
};
